import { useCallback, useEffect, useMemo, useState } from "react";
import { listarCursos } from "../../../../services/cursosService";
import { listarCentros } from "../../../../services/centrosService";
import { listarNiveles } from "../../../../services/nivelesService";
import { listarEspecialidades } from "../../../../services/especialidadesService";
import { normalizeCurso, resolveNivelId } from "../adapters/cursosAdapter";
import { initialCursosFilters } from "../constants/cursos";
import { showToast } from "../../../../utils/showToast";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function useCursosList() {
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [centros, setCentros] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);

  const [centrosLoading, setCentrosLoading] = useState(false);
  const [nivelesLoading, setNivelesLoading] = useState(false);
  const [especialidadesLoading, setEspecialidadesLoading] = useState(false);

  const [centrosError, setCentrosError] = useState(null);
  const [nivelesError, setNivelesError] = useState(null);
  const [especialidadesError, setEspecialidadesError] = useState(null);

  const [filtros, setFiltros] = useState(initialCursosFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchCursos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarCursos();
      setCursos((Array.isArray(data) ? data : []).map((item) => normalizeCurso(item)));
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo obtener los cursos.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCentros = useCallback(async () => {
    setCentrosLoading(true);
    try {
      const data = await listarCentros();
      setCentros(Array.isArray(data) ? data : []);
      setCentrosError(null);
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo cargar la lista de centros.";
      setCentrosError(message);
      showToast({ type: "warning", message });
    } finally {
      setCentrosLoading(false);
    }
  }, []);

  const fetchNiveles = useCallback(async () => {
    setNivelesLoading(true);
    try {
      const data = await listarNiveles();
      setNiveles(Array.isArray(data) ? data : []);
      setNivelesError(null);
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo cargar los niveles.";
      setNivelesError(message);
      showToast({ type: "warning", message });
    } finally {
      setNivelesLoading(false);
    }
  }, []);

  const fetchEspecialidades = useCallback(async () => {
    setEspecialidadesLoading(true);
    try {
      const data = await listarEspecialidades();
      setEspecialidades(Array.isArray(data) ? data : []);
      setEspecialidadesError(null);
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo cargar las especialidades.";
      setEspecialidadesError(message);
      showToast({ type: "warning", message });
    } finally {
      setEspecialidadesLoading(false);
    }
  }, []);

  useEffect(() => {
    // Escalonadas: disparar las 4 a la vez (mas lo que pida otro widget de la
    // misma pagina, ej. contifico/productos) es la rafaga que provoca 404
    // intermitentes bajo trafico concurrente en produccion.
    let cancelled = false;
    fetchCursos();
    wait(150).then(() => { if (!cancelled) fetchCentros(); });
    wait(300).then(() => { if (!cancelled) fetchNiveles(); });
    wait(450).then(() => { if (!cancelled) fetchEspecialidades(); });
    return () => {
      cancelled = true;
    };
  }, [fetchCursos, fetchCentros, fetchNiveles, fetchEspecialidades]);

  const resumen = useMemo(() => {
    const total = cursos.length;
    const activos = cursos.filter((curso) => curso.activo).length;
    const nivel1 = cursos.filter((curso) => String(resolveNivelId(curso, niveles) || "") === "1").length;
    const nivel2 = cursos.filter((curso) => String(resolveNivelId(curso, niveles) || "") === "2").length;
    const inactivos = Math.max(total - activos, 0);
    return { total, activos, inactivos, nivel1, nivel2 };
  }, [cursos, niveles]);

  const cursosFiltrados = useMemo(() => {
    return cursos.filter((curso) => {
      const nivelResuelto = resolveNivelId(curso, niveles);
      if (filtros.centro_id && String(curso.centroId || "") !== String(filtros.centro_id)) return false;
      if (filtros.nivel_id && String(nivelResuelto || "") !== String(filtros.nivel_id)) return false;
      if (filtros.activo === "1" && !curso.activo) return false;
      if (filtros.activo === "0" && curso.activo) return false;
        if (filtros.search) {
        const term = filtros.search.toLowerCase();
        const hayCoincidencia =
          (curso.nombre || "").toLowerCase().includes(term) ||
          (curso.codigoCurso || "").toLowerCase().includes(term) ||
          (curso.centroNombre || "").toLowerCase().includes(term) ||
          (curso.nivelNombre || "").toLowerCase().includes(term) ||
          (curso.especialidadNombre || "").toLowerCase().includes(term);
        if (!hayCoincidencia) return false;
      }
      return true;
    });
  }, [cursos, filtros, niveles]);

  const total = cursosFiltrados.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filtros, total]);

  const cursosPaginados = useMemo(() => {
    const start = (page - 1) * pageSize;
    return cursosFiltrados.slice(start, start + pageSize);
  }, [cursosFiltrados, page, pageSize]);

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const quickFilter = useCallback((key) => {
    switch (key) {
      case "activos":
        setFiltros((prev) => ({ ...prev, activo: "1", nivel_id: "" }));
        break;
      case "inactivos":
        setFiltros((prev) => ({ ...prev, activo: "0", nivel_id: "" }));
        break;
      case "nivel1":
        setFiltros((prev) => ({ ...prev, nivel_id: "1", activo: "all" }));
        break;
      case "nivel2":
        setFiltros((prev) => ({ ...prev, nivel_id: "2", activo: "all" }));
        break;
      default:
        setFiltros(initialCursosFilters);
        break;
    }
  }, []);

  const activeQuickFilter = useMemo(() => {
    if (filtros.activo === "1") return "activos";
    if (filtros.activo === "0") return "inactivos";
    if (String(filtros.nivel_id) === "1") return "nivel1";
    if (String(filtros.nivel_id) === "2") return "nivel2";
    return "total";
  }, [filtros]);

  const limpiarFiltros = useCallback(() => setFiltros(initialCursosFilters), []);

  const upsertCurso = useCallback((curso) => {
    if (!curso?.id) return;
    setCursos((prev) => {
      const exists = prev.some((item) => String(item.id) === String(curso.id));
      if (exists) {
        return prev.map((item) =>
          String(item.id) === String(curso.id) ? normalizeCurso({ ...item, ...curso }) : item
        );
      }
      return [normalizeCurso(curso), ...prev];
    });
    setLastUpdated(new Date());
  }, []);

  const removeCurso = useCallback((id) => {
    setCursos((prev) => prev.filter((curso) => String(curso.id) !== String(id)));
    setLastUpdated(new Date());
  }, []);

  const loadingCatalogos = centrosLoading || nivelesLoading || especialidadesLoading;

  return {
    cursos: cursosFiltrados,
    cursosOriginales: cursos,
    cursosPaginados,
    resumen,
    filtros,
    setFiltros,
    quickFilter,
    activeQuickFilter,
    limpiarFiltros,
    loading,
    error,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    centros,
    niveles,
    especialidades,
    centrosLoading,
    nivelesLoading,
    especialidadesLoading,
    centrosError,
    nivelesError,
    especialidadesError,
    loadingCatalogos,
    reload: fetchCursos,
    reloadCentros: fetchCentros,
    reloadNiveles: fetchNiveles,
    reloadEspecialidades: fetchEspecialidades,
    upsertCurso,
    removeCurso,
  };
}

export { initialCursosFilters as initialCursoFilters };
